/**
 * Toast Notification Store
 *
 * Manages transient, auto-dismissing notifications (toasts) for
 * action confirmations, errors, and success messages.
 *
 * Distinct from useNotificationStore which manages persistent
 * in-app notifications (notification bell/inbox).
 */

import { create } from 'zustand'

// ── Types ─────────────────────────────────────────────────────────────

export type ToastVariant = 'default' | 'success' | 'error' | 'warning'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface Toast {
  id: string
  title: string
  description?: string
  variant: ToastVariant
  duration: number
  createdAt: number
  action?: ToastAction
}

interface ToastState {
  toasts: Toast[]
}

interface ToastActions {
  addToast: (toast: Omit<Toast, 'id' | 'createdAt'>) => string
  removeToast: (id: string) => void
  clearAll: () => void
}

// ── Helpers ───────────────────────────────────────────────────────────

let counter = 0
function nextId(): string {
  return `toast-${++counter}-${Date.now()}`
}

// ── Store ─────────────────────────────────────────────────────────────

export const useToastStore = create<ToastState & ToastActions>((set) => ({
  toasts: [],

  addToast: (toast) => {
    const id = nextId()
    const newToast: Toast = {
      ...toast,
      id,
      createdAt: Date.now(),
    }

    set((state) => ({
      toasts: [...state.toasts, newToast].slice(-5), // max 5 visible
    }))

    // Auto-dismiss
    if (toast.duration > 0) {
      setTimeout(() => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        }))
      }, toast.duration)
    }

    return id
  },

  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),

  clearAll: () => set({ toasts: [] }),
}))

// ── Convenience functions (callable outside React) ────────────────────

export function toast(title: string, options?: { description?: string; variant?: ToastVariant; duration?: number }) {
  return useToastStore.getState().addToast({
    title,
    description: options?.description,
    variant: options?.variant ?? 'default',
    duration: options?.duration ?? 4000,
  })
}

toast.success = (title: string, description?: string) =>
  toast(title, { variant: 'success', description })

toast.error = (title: string, description?: string) =>
  toast(title, { variant: 'error', description, duration: 6000 })

toast.warning = (title: string, description?: string) =>
  toast(title, { variant: 'warning', description })

// ── Selectors ─────────────────────────────────────────────────────────

export const selectToasts = (state: ToastState) => state.toasts
